import { format, parseISO } from "date-fns";
import { ArrowRight, CalendarPlus, EyeOff, Eye, CheckCircle2, Clock, Ban } from "lucide-react";
import { Link } from "react-router-dom";

const fmtD = d => { try { return format(parseISO(d), "EEE d MMM yyyy"); } catch { return d || "TBC"; } };

const RESPONSE = {
  awaiting: ["Coach to confirm", "text-amber-300 border-amber-400/50", Clock],
  confirmed: ["Coach confirmed — booking moved", "text-[#83B68B] border-[#83B68B]/50", CheckCircle2],
  kept: ["Coach kept original booking", "opacity-70 border-border", Ban],
};

export function FAUpdateRow({ r, tab, isAdmin, selected, onSelect, onStage, onIgnore, onUnignore, onCancelBooking }) {
  const prev = r.previous_snapshot;
  const resp = r.coach_response ? RESPONSE[r.coach_response] : null;
  const RespIcon = resp ? resp[2] : null;
  const ignored = r.review_status === "ignored";
  const canSelect = isAdmin && tab === "new" && r.review_status === "pending" && !r.staging;
  return (
    <div className={`card-tactical rounded-md p-4 ${ignored ? "opacity-50" : ""}`} data-testid={`fa-update-${r.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        {canSelect && <input type="checkbox" checked={selected} onChange={onSelect} data-testid={`fa-update-select-${r.id}`} className="mt-1 accent-[#83B68B]" />}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 text-xs opacity-60">
            <span className="uppercase tracking-widest">{r.league_name}</span>
            {r.competition && <span>· {r.competition}</span>}
            {r.matched_age_group && <span>· {r.matched_age_group}</span>}
            {!r.matched_team_id && <span className="text-amber-300">· team not matched — map it in Admin → Leagues</span>}
          </div>
          <div className="font-display font-bold text-lg mt-0.5">
            {r.home_team} <span className="opacity-40 font-normal">v</span> {r.away_team}
          </div>
          <div className="text-sm mt-1 flex flex-wrap items-center gap-2">
            {r.status_label === "changed" && prev ? (
              <>
                <span className="opacity-60 line-through">{fmtD(prev.date)} {prev.kickoff}</span>
                <ArrowRight className="w-3 h-3 opacity-50" />
                <span className="text-[#83B68B] font-semibold">{fmtD(r.date)} {r.kickoff}</span>
              </>
            ) : (
              <span className={r.status_label === "cancelled" ? "line-through opacity-60" : ""}>{fmtD(r.date)} · {r.kickoff || "TBC"}</span>
            )}
            {!r.is_home_eyfc && r.venue && <span className="opacity-60">· {r.venue}</span>}
            {r.status_label === "cancelled" && <span className="text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border border-red-400/50 text-red-300">No longer listed by FA</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
            {r.booking && (
              <Link to="/app/bookings" className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-border hover:border-[#83B68B]" data-testid={`fa-update-booking-${r.id}`}>
                Booked · {r.booking.venue_short_code ? `[${r.booking.venue_short_code}] ` : ""}{r.booking.pitch_name} · {fmtD(r.booking.booking_date)} {String(r.booking.start_hour).padStart(2, "0")}:{String(r.booking.start_min || 0).padStart(2, "0")}
              </Link>
            )}
            {resp && <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${resp[1]}`} data-testid={`fa-update-response-${r.id}`}><RespIcon className="w-3 h-3" /> {resp[0]}</span>}
            {r.staging && <span className="px-2 py-0.5 rounded border border-sky-400/50 text-sky-200" data-testid={`fa-update-staged-${r.id}`}>In allocation · {r.staging.proposal_status}{r.staging.pitch_name ? ` · ${r.staging.pitch_name}` : ""}</span>}
            {ignored && <span className="px-2 py-0.5 rounded border border-border">Ignored</span>}
          </div>
        </div>
        {isAdmin && tab === "cancelled" && r.booking && (
          <button type="button" onClick={() => { if (window.confirm(`Cancel the pitch booking for ${r.home_team} v ${r.away_team}? The coach and anyone sharing will be emailed.`)) onCancelBooking(); }} data-testid={`fa-update-cancel-booking-${r.id}`} className="px-3 py-1.5 rounded border border-red-400/50 text-red-300 text-[10px] tracking-widest uppercase inline-flex items-center gap-1 hover:bg-red-500/10"><Ban className="w-3 h-3" /> Cancel booking</button>
        )}
        {tab === "cancelled" && !r.booking && r.review_status === "booking_cancelled" && <span className="text-[10px] tracking-widest uppercase opacity-60" data-testid={`fa-update-booking-cancelled-${r.id}`}>Booking cancelled</span>}
        {isAdmin && tab === "new" && !r.booking && !r.staging && (
          <div className="flex gap-1.5 flex-wrap">
            {ignored ? (
              <button type="button" onClick={onUnignore} data-testid={`fa-update-unignore-${r.id}`} className="px-3 py-1.5 rounded border border-border text-[10px] tracking-widest uppercase inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Restore</button>
            ) : (
              <>
                <button type="button" onClick={onStage} data-testid={`fa-update-stage-${r.id}`} className="btn-volt px-3 py-1.5 rounded text-[10px] tracking-widest uppercase inline-flex items-center gap-1"><CalendarPlus className="w-3 h-3" /> Send to allocation</button>
                <button type="button" onClick={onIgnore} data-testid={`fa-update-ignore-${r.id}`} className="px-3 py-1.5 rounded border border-border text-[10px] tracking-widest uppercase inline-flex items-center gap-1 opacity-70 hover:opacity-100"><EyeOff className="w-3 h-3" /> Ignore</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
